// ─────────────────────────────────────────────
//  SetupPage — Admin-only setup and configuration
//  Styles extracted to setupPage.styles.js (Plan 13, dark→light token restyle).
// ─────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, onSnapshot, query, where } from "../firebase/sdk";
import { db } from "../firebase/config";
import { listenToAssignments, listenToStaff, setDailyRoster } from "../firebase/firestore";
import { useDailyRoster, rosterDateKey } from "../hooks/useDailyRoster";
import { myRoomAssignments } from "../domain/myRooms";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { ACCESS_LEVELS, VIEW_AS_OPTIONS, accessLevelOf } from "../config/roles";
import { TEST_USERS, testUserById } from "../config/testUsers";
import { useFacilities } from "../hooks/useFacilities";
import Modal from "../components/Modal";
import Avatar from "../components/Avatar";
import SignOutButton from "../components/SignOutButton";
import AccessDenied from "../components/AccessDenied";
import NavMenu from "../components/NavMenu";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";
import { FacilityTab } from "./setupPage.facility";
import TestDayPanel from "../features/testing/TestDayPanel";
import { AddFacilityModal } from "./setupPage.addFacility";
import { RoomsTab, UsersTab, PackageTab, NotificationsTab } from "./setupPage.tabs";
import { ProviderMapTab } from "./setupPage.map";
import { MobileAppSection } from "./setupPage.mobileApp";

const { color } = tokens;

export default function SetupPage() {
  const { user, isAdmin, isAIC, isDoctor } = useAuth();
  const navigate = useNavigate();
  // Active setup section lives in the URL (?tab=id) so the NavMenu "Setup ▸"
  // submenu can open any section directly. Defaults to Facility.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "facility";
  const setTab = (id) => setSearchParams({ tab: id });
  // Selected facility for the whole setup area — Facility info, Locations,
  // Surgeons, Specialties, and Rooms all read from this one selection so they
  // stay in sync. Defaults to the user's active facility.
  const [facilityId, setFacilityId] = useState(user?.activeFacilityId || "default");
  const [addOpen, setAddOpen] = useState(false);
  const facilities = useFacilities();

  if (!isAdmin && !isAIC) {
    return <AccessDenied body="Setup is for administrators and the AIC. Ask an administrator if you need something changed here." />;
  }

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={{display:"flex", gap:12, alignItems:"center", minWidth:0}}>
          <span style={s.logo}>⚕ ATC</span>
          <NavMenu />
        </div>
        <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap"}}>
          <label style={{fontSize:11, fontWeight:700, color:color.text.muted, textTransform:"uppercase", letterSpacing:".5px"}} htmlFor="setup-facility">Facility</label>
          <select id="setup-facility" style={{...s.select, width:"auto", minWidth:200, padding:"6px 10px", fontWeight:700}}
            value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.label}{f.isActive === false ? " (inactive)" : ""}</option>)}
          </select>
          {isAdmin && (
            <button style={s.primaryBtn} onClick={() => setAddOpen(true)}>＋ Add Facility</button>
          )}
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      <div style={s.layout}>
        {/* CONTENT — section nav now lives in the topbar NavMenu "Setup ▸" submenu. */}
        <div style={s.main}>
          {tab === "facility"      && <FacilityTab facilityId={facilityId} />}
          {tab === "rooms"         && <RoomsTab facilityId={facilityId} />}
          {tab === "users"         && <UsersTab />}
          {tab === "aic"           && <AICTab />}
          {tab === "map"           && <ProviderMapTab />}
          {tab === "mobile"        && <><MobileAppSection facilityId={facilityId} /><MobilePreviewTab /></>}
          {tab === "testing"       && <><TestingTab /><MobilePreviewTab /></>}
          {tab === "package"       && <PackageTab />}
          {tab === "notifications" && <NotificationsTab />}
        </div>
      </div>

      <AddFacilityModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        facilities={facilities}
        onCreated={(id) => { setFacilityId(id); setTab("facility"); }}
      />
    </div>
  );
}

// ── TAB: AIC ──────────────────────────────────
// RoomsTab, UsersTab, PackageTab, NotificationsTab live in setupPage.tabs.js.
// ── TAB: MOBILE PREVIEW ───────────────────────
// The live app rendered inside a phone-sized frame so you can see exactly what
// an iPhone/Android user sees, without a device. The iframe is the same origin,
// so it shares your login; at <768px the app renders its mobile layout.
const PREVIEW_DEVICES = [
  { id: "iphone", label: "iPhone", w: 390, h: 844 },
  { id: "android", label: "Android", w: 360, h: 800 },
  { id: "small", label: "Small", w: 320, h: 568 },
];
const PREVIEW_ROUTES = [
  { path: "/board", label: "OR Board" },
  { path: "/room", label: "My Room" },
  { path: "/daily-list", label: "Daily List" },
];
const previewLabelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 6 };

function MobilePreviewTab() {
  const [device, setDevice] = useState(PREVIEW_DEVICES[0]);
  const [route, setRoute] = useState(PREVIEW_ROUTES[0].path);
  const [nonce, setNonce] = useState(0); // bumps to reload the iframe

  return (
    <div>
      <div style={s.tabHeader}>
        <div>
          <div style={s.tabTitle}>Mobile Preview</div>
          <div style={s.tabSub}>See how each screen looks on a phone — the live app at phone width. (It shares your login; actions inside are real.)</div>
        </div>
      </div>
      <div style={s.panel}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
          <div>
            <span style={previewLabelStyle}>Device</span>
            <div style={{ display: "flex", gap: 6 }}>
              {PREVIEW_DEVICES.map((d) => (
                <button key={d.id}
                  style={{ ...s.outlineBtn, ...(device.id === d.id ? { borderColor: color.accent.primary, color: color.accent.primary, fontWeight: 700 } : {}) }}
                  onClick={() => setDevice(d)} aria-pressed={device.id === d.id}>
                  {d.label} · {d.w}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span style={previewLabelStyle}>Screen</span>
            <select style={{ ...s.select, width: "auto", minWidth: 160 }} value={route} onChange={(e) => setRoute(e.target.value)} aria-label="Screen to preview">
              {PREVIEW_ROUTES.map((r) => <option key={r.path} value={r.path}>{r.label}</option>)}
            </select>
          </div>
          <button style={s.ghostBtn} onClick={() => setNonce((n) => n + 1)}>↻ Reload</button>
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: device.w + 16, height: device.h + 16, background: "#0b1020", borderRadius: 36, padding: 8, boxShadow: "0 12px 40px rgba(0,0,0,.22)" }}>
            <iframe
              key={`${route}-${device.id}-${nonce}`}
              title={`Mobile preview — ${route}`}
              src={route}
              style={{ width: device.w, height: device.h, border: 0, borderRadius: 28, background: "#fff", display: "block" }}
            />
          </div>
        </div>
        <p style={{ fontSize: 12, color: color.text.muted, textAlign: "center", marginTop: 12 }}>
          Live preview at {device.w}×{device.h}. The app inside renders its mobile layout (under 768px wide).
        </p>
      </div>
    </div>
  );
}

function AICTab() {
  const { realUser } = useAuth();
  const toast = useToast();
  const facilityId = realUser?.activeFacilityId || "default";
  const dateKey = rosterDateKey();
  const { roster } = useDailyRoster(facilityId, dateKey);
  const [busy, setBusy] = useState(false);

  // The real AIC the live board uses = daily_boards.aicId for today.
  const aicId = roster.aicId;
  const supervisors = roster.supervisors || [];
  const currentAic =
    supervisors.find((sup) => sup.id === aicId) ||
    (aicId ? { id: aicId, name: roster.aicName || "AIC" } : null);
  // Transfer candidates = the day's other rostered doctors.
  const candidates = supervisors.filter((sup) => sup.id !== aicId);
  const initials = (name) =>
    String(name || "")
      .replace(/^dr\.?\s+/i, "")
      .split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

  const transfer = async (doc) => {
    setBusy(true);
    try {
      await setDailyRoster(facilityId, dateKey, { aicId: doc.id, aicName: doc.name }, realUser?.uid);
      toast.success(`AIC transferred to ${doc.name}.`);
    } catch (err) {
      console.error("Transfer AIC failed:", err);
      toast.error("Couldn't transfer the AIC. Check your connection.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={s.tabHeader}>
        <div><div style={s.tabTitle}>AIC Assignment</div><div style={s.tabSub}>Designate today's Anesthesiologist In Charge — the same AIC the live board uses.</div></div>
      </div>
      <div style={s.panel}>
        <div style={s.panelTitle}>⭐ Today's AIC</div>
        {currentAic ? (
          <div style={{display:"flex", alignItems:"center", gap:14, padding:14, background:`${color.accent.closing}12`, border:`1px solid ${color.accent.closing}44`, borderRadius:10, marginBottom:16}}>
            <div style={{...s.pav, width:44, height:44, fontSize:16, background:"#e8edf9", color:color.accent.primary, border:`1px solid ${color.accent.primary}`}}>{initials(currentAic.name)}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:16, fontWeight:800}}>{currentAic.name}</div>
              <div style={{fontSize:12, color:color.text.secondary}}>AIC for {dateKey}</div>
            </div>
            <div style={{background:`${color.accent.closing}20`, border:`1px solid ${color.accent.closing}`, color:color.accent.closing, borderRadius:20, padding:"4px 14px", fontSize:12, fontWeight:700}}>Current AIC</div>
          </div>
        ) : (
          <p style={{fontSize:13, color:color.text.secondary, margin:"4px 0 16px"}}>
            No AIC set for today. Pick one below, or set it on the Daily List.
          </p>
        )}
        <div style={{fontSize:13, color:color.text.secondary, marginBottom:10}}>Transfer AIC to:</div>
        {candidates.length ? (
          <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
            {candidates.map((d) => (
              <button key={d.id} style={s.outlineBtn} disabled={busy} onClick={() => transfer(d)}>{d.name}</button>
            ))}
          </div>
        ) : (
          <p style={{fontSize:12, color:color.text.muted, margin:0}}>
            No other doctors on today's roster. Add supervising doctors on the Daily List first.
          </p>
        )}
      </div>
    </div>
  );
}

// ── TAB: TESTING MODE ─────────────────────────
// Admin-only "View as" — step into any tier to see exactly what it sees,
// without needing a separate test account. Writes are blocked while previewing
// (see firestore.js), so it is safe to use against live data.
function TestingTab() {
  const { realUser, setViewAs, isPreviewing, viewAs, testingMode, setTestingMode } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Live directory of real users in this facility — the source for the
  // "View as a specific person" picker. Read-only; never mutated here.
  const facilityId = realUser?.activeFacilityId || "default";
  const [dirUsers, setDirUsers] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "users"), where("facilityIds", "array-contains", facilityId)),
      (snap) => setDirUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Load directory for testing picker failed:", err)
    );
    return unsub;
  }, [facilityId]);

  // Today's assignments + staff — used to resolve each person's assigned
  // room(s) the SAME way "My Room" does (myRoomAssignments), so the picker and
  // the previewed page agree. Date is today's UTC key (matches the board/grid).
  const dateKey = rosterDateKey();
  // Today's roster names the board AIC (daily_boards.aicId/aicName) — the source
  // of truth for who the AIC is, NOT the persistent users.isAIC flag.
  const { roster } = useDailyRoster(facilityId, dateKey);
  const [assignments, setAssignments] = useState([]);
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    const unsub = listenToAssignments(
      facilityId,
      dateKey,
      (snap) => setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [facilityId, dateKey]);
  useEffect(() => {
    const unsub = listenToStaff(
      (snap) => setStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Load staff for testing picker failed:", err)
    );
    return unsub;
  }, []);

  // The tier whose staff picker is open (null = closed). Observer skips the
  // picker because it has no per-person assignments — it stays a generic view.
  const [pickerTier, setPickerTier] = useState(null);
  const pickerStaff = useMemo(() => {
    if (!pickerTier) return [];
    // The AIC tier is driven by today's board AIC (daily_boards), matched by id
    // or name — not the static users.isAIC flag (which is usually unset).
    const aicId = roster?.aicId || null;
    const aicNameLc = (roster?.aicName || "").trim().toLowerCase();
    const isBoardAic = (u) =>
      (aicId && u.id === aicId) || (aicNameLc && (u.name || "").trim().toLowerCase() === aicNameLc);
    return dirUsers
      .filter((u) =>
        pickerTier === "aic"
          ? isBoardAic(u) || accessLevelOf({ role: u.role, isAIC: u.isAIC }) === "aic"
          : accessLevelOf({ role: u.role, isAIC: u.isAIC }) === pickerTier
      )
      .map((u) => {
        // Resolve the person's rooms from today's assignments (u.id === uid for
        // a users doc), exactly like the My Room page.
        const mine = myRoomAssignments(assignments, { ...u, uid: u.id }, staff);
        const roomNames = [...new Set(mine.map((a) => a.orRoom).filter(Boolean))];
        // Previewing from the AIC tier grants AIC for the session.
        return { ...u, isAIC: pickerTier === "aic" ? true : u.isAIC, roomNames };
      })
      .sort((a, b) => {
        // Working today (has assigned rooms) first, then alphabetical.
        const ra = a.roomNames.length ? 0 : 1;
        const rb = b.roomNames.length ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [pickerTier, dirUsers, assignments, staff, roster]);

  // Generic tier preview (no specific identity) — used for Observer and as the
  // explicit fallback when a role has no real staff yet.
  const start = (key) => {
    setViewAs(key);
    setPickerTier(null);
    toast.info(`Now viewing as ${ACCESS_LEVELS[key]?.label || key}. Exit from the banner at the top.`);
    navigate("/board"); // setup is admin-only, so drop into the role's real home
  };

  // Preview as one specific real person — their name, assigned room, and exact
  // permissions. Writes are still intercepted (see firestore.js / banner).
  const startRealUser = (u) => {
    setViewAs({
      uid: u.id,
      name: u.name,
      role: u.role,
      isAIC: !!u.isAIC,
      subRole: u.subRole ?? null,
      currentRoomId: u.currentRoomId ?? null,
      currentRoomName: u.roomNames?.[0] ?? u.currentRoomName ?? null,
    });
    setPickerTier(null);
    toast.info(`Now viewing as ${u.name}. Exit from the banner at the top.`);
    navigate("/board");
  };

  // Tier buttons open the person picker; Observer goes straight to generic view.
  const onTierClick = (key) => (key === "observer" ? start(key) : setPickerTier(key));

  const startTestUser = (tu) => {
    setViewAs(tu.id);
    toast.info(`Now viewing as ${tu.name}. Exit from the banner at the top.`);
    navigate("/board");
  };

  return (
    <div>
      <div style={s.tabHeader}>
        <div>
          <div style={s.tabTitle}>Testing Mode</div>
          <div style={s.tabSub}>
            Preview the app as any role — no separate accounts needed. You stay
            signed in as <strong>{realUser?.name || "an administrator"}</strong>;
            changes are not saved while previewing.
          </div>
        </div>
      </div>

      {isPreviewing && (
        <div style={{...s.panel, border:`1px solid ${color.accent.closing}`, background:`${color.accent.closing}12`}}>
          <div style={{fontSize:13, color:color.accent.closing, fontWeight:700}}>
            👁️ Currently previewing as {ACCESS_LEVELS[viewAs]?.label || testUserById(viewAs)?.name || viewAs}.
            Use “Exit preview” in the top banner to return.
          </div>
        </div>
      )}

      {/* Testing-mode switch — controls whether the testing accounts exist. */}
      <div style={{...s.panel, display:"flex", alignItems:"center", justifyContent:"space-between", gap:14}}>
        <div>
          <div style={s.panelTitle}>Testing accounts</div>
          <p style={{fontSize:12, color:color.text.secondary, margin:"6px 0 0", lineHeight:1.5}}>
            When ON, the Testing/Dummy accounts below appear in every staff dropdown
            and assignment list on this device. When OFF, they are completely hidden.
          </p>
        </div>
        <button
          role="switch"
          aria-checked={testingMode}
          aria-label="Testing mode"
          onClick={() => {
            setTestingMode(!testingMode);
            toast.info(testingMode ? "Testing mode OFF — test accounts hidden." : "Testing mode ON — test accounts available.");
          }}
          style={{
            ...s.primaryBtn,
            minWidth: 120,
            justifyContent: "center",
            background: testingMode ? color.accent.active : color.text.muted,
          }}
        >
          {testingMode ? "ON" : "OFF"}
        </button>
      </div>

      {/* ── TEST A FACILITY ON A DATE ────────────────────────────────────
          Sits ABOVE the testing accounts because it answers a different
          question: those are synthetic people on today's board, this is a real
          day replayed with the real people who worked it. It is deliberately
          NOT gated on `testingMode` — that toggle controls whether the dummy
          accounts appear in staff dropdowns, which has nothing to do with
          replaying a past day. */}
      <TestDayPanel />

      {testingMode && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14, marginBottom:16}}>
          {TEST_USERS.map((tu) => {
            const meta = ACCESS_LEVELS[accessLevelOf(tu)];
            const active = isPreviewing && viewAs === tu.id;
            return (
              <div key={tu.id} style={{...s.panel, marginBottom:0, ...(active ? {border:`1px solid ${meta.color}`} : {})}}>
                <div style={{fontSize:15, fontWeight:800, display:"flex", alignItems:"center", gap:8}}>
                  <span aria-hidden="true">{meta.icon}</span>
                  <span style={{color: meta.color}}>{tu.name}</span>
                </div>
                <p style={{fontSize:12, color:color.text.secondary, margin:"8px 0 16px", lineHeight:1.5}}>
                  {tu.description} <em>({meta.short})</em>
                </p>
                <button style={{...s.primaryBtn, width:"100%", justifyContent:"center"}} onClick={() => startTestUser(tu)}>
                  {active ? "Re-enter this view" : `View as ${tu.name}`}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div style={{display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14}}>
        {VIEW_AS_OPTIONS.map((opt) => {
          const meta = ACCESS_LEVELS[opt.key];
          const active = isPreviewing && viewAs === opt.key;
          return (
            <div key={opt.key} style={{...s.panel, marginBottom:0, ...(active ? {border:`1px solid ${meta.color}`} : {})}}>
              <div style={{fontSize:15, fontWeight:800, display:"flex", alignItems:"center", gap:8}}>
                <span aria-hidden="true">{meta.icon}</span>
                <span style={{color: meta.color}}>{meta.label}</span>
              </div>
              <p style={{fontSize:12, color:color.text.secondary, margin:"8px 0 16px", lineHeight:1.5}}>{meta.description}</p>
              <button style={{...s.primaryBtn, width:"100%", justifyContent:"center"}} onClick={() => onTierClick(opt.key)}>
                {opt.key === "observer"
                  ? (active ? "Re-enter this view" : "View as Observer")
                  : `View as a specific ${meta.short}…`}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{...s.panel, marginTop:16}}>
        <div style={s.panelTitle}>How it works</div>
        <ul style={{fontSize:12, color:color.text.secondary, lineHeight:1.7, paddingLeft:18, margin:0}}>
          <li>The board, navigation, and actions render exactly as that role sees them.</li>
          <li>Any save, status change, break, or alert is intercepted — nothing is written to live data.</li>
          <li>The amber banner stays on screen the whole time. Click “Exit preview” to return to your admin view.</li>
          <li>Only administrators can enter testing mode.</li>
        </ul>
      </div>

      <Modal
        isOpen={!!pickerTier}
        onClose={() => setPickerTier(null)}
        title={`View as a specific ${pickerTier ? ACCESS_LEVELS[pickerTier]?.short : ""}`}
      >
        <p style={{margin:"0 0 14px", fontSize:13, color:color.text.secondary, lineHeight:1.5}}>
          Pick the staff member to preview. You'll see their name, assigned room,
          and exact permissions — changes are still saved as you (Administrator).
        </p>
        {pickerStaff.length === 0 ? (
          <div>
            <div style={{padding:"16px 0", textAlign:"center", color:color.text.muted, fontSize:13}}>
              No {pickerTier ? ACCESS_LEVELS[pickerTier]?.label : ""} found in this facility.
            </div>
            <button style={{...s.ghostBtn, width:"100%", justifyContent:"center"}} onClick={() => start(pickerTier)}>
              Preview a generic {pickerTier ? ACCESS_LEVELS[pickerTier]?.short : ""} view instead
            </button>
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:8, maxHeight:340, overflowY:"auto"}}>
            {pickerStaff.map((u) => {
              const meta = ACCESS_LEVELS[accessLevelOf({ role: u.role, isAIC: u.isAIC })];
              const room = u.roomNames?.length
                ? u.roomNames.join(", ")
                : (u.currentRoomName || u.currentRoomId);
              return (
                <button
                  key={u.id}
                  onClick={() => startRealUser(u)}
                  style={{
                    display:"flex", alignItems:"center", gap:12, width:"100%", textAlign:"left",
                    padding:"10px 12px", borderRadius:8, cursor:"pointer",
                    border:`1px solid ${color.border.default}`, background:color.surface.raised,
                  }}
                >
                  <span aria-hidden="true" style={{fontSize:16}}>{meta?.icon}</span>
                  <span style={{flex:1, minWidth:0}}>
                    <span style={{display:"block", fontSize:13, fontWeight:700, color:color.text.primary}}>{u.name}</span>
                    <span style={{display:"block", fontSize:11, color:color.text.secondary}}>
                      {meta?.label}{room ? ` · ${room}` : " · no room assigned"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}

// PackageTab and NotificationsTab live in setupPage.tabs.js.
